import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Building2,
  Search,
  MapPin,
  Users,
  Calendar,
  Phone,
  Mail,
  ExternalLink,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesSchools() {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchSchools();
  }, [statusFilter]);

  const fetchSchools = async () => {
    try {
      let url = `${API}/api/sales/schools`;
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();

      if (data.success) {
        setSchools(data.schools);
      }
    } catch (error) {
      console.error("Failed to fetch schools:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      suspended: "bg-red-100 text-red-700",
      trial: "bg-blue-100 text-blue-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const filteredSchools = schools.filter(
    (school) =>
      school.name?.toLowerCase().includes(search.toLowerCase()) ||
      school.city?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schools Directory</h1>
          <p className="text-gray-500">{schools.length} registered schools</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or city..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-gray-50"
                  data-testid="search-schools-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("")}
                className={statusFilter === "" ? "bg-blue-600" : ""}
              >
                All
              </Button>
              {["active", "pending", "trial"].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? "bg-blue-600" : ""}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schools List */}
      {filteredSchools.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No schools found</h3>
            <p className="text-gray-500">
              {search || statusFilter
                ? "Try adjusting your filters"
                : "No schools registered yet"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSchools.map((school) => (
            <Card
              key={school.id || school.name}
              className="bg-white shadow-sm border-gray-200 hover:shadow-md transition-shadow"
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <Badge className={getStatusColor(school.status)}>
                    {school.status}
                  </Badge>
                </div>

                <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                  {school.name}
                </h3>

                {(school.city || school.state) && (
                  <p className="text-sm text-gray-500 flex items-center gap-1 mb-3">
                    <MapPin className="w-3 h-3" />
                    {[school.city, school.state].filter(Boolean).join(", ")}
                  </p>
                )}

                <div className="space-y-2 text-sm mb-4">
                  {school.students_count !== undefined && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span>{school.students_count} students</span>
                    </div>
                  )}
                  {school.contact_email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{school.contact_email}</span>
                    </div>
                  )}
                  {school.contact_phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{school.contact_phone}</span>
                    </div>
                  )}
                  {school.created_at && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>Joined {new Date(school.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {school.subscription && (
                  <div className="p-3 bg-gray-50 rounded-lg mb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Subscription
                    </p>
                    <p className="font-medium text-gray-900">
                      {school.subscription.plan || "N/A"}
                    </p>
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    /* TODO: View school details */
                  }}
                >
                  View Details
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
